import {
	Home,
	TrendingUp,
	Sparkles,
	Star,
	Library,
	Upload,
	LayoutDashboard,
	X,
	ChevronDown,
	Gamepad2,
	Zap,
	Puzzle,
	Swords,
	Rocket,
	Trophy,
	Flame,
	Search,
	Coffee,
	StarIcon,
	StarHalfIcon,
} from "lucide-react";

import { useState } from "react";
import { NavLink } from "./NavLink";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

const navSections = [
	{
		title: "Discover",
		defaultOpen: true,
		items: [
			{
				title: "Home",
				to: "/",
				matchSearch: {
					sort: null,
					genre: null,
					size: null,
					tag: null,
				},
				icon: Home,
				description: "Browse all games",
			},
			{
				title: "Trending",
				to: {
					pathname: "/",
					search: "?sort=trending",
				},
				matchSearch: {
					sort: "trending",
				},
				icon: Flame,
				description: "Most active today",
			},
			{
				title: "Popular",
				to: "/popular",
				icon: TrendingUp,
				description: "Community favorites",
			},
			{
				title: "Top Rated",
				to: {
					pathname: "/",
					search: "?sort=top-rated",
				},
				matchSearch: {
					sort: "top-rated",
				},
				icon: StarHalfIcon,
				description: "Highest rated games",
			},
			{
				title: "New Games",
				to: {
					pathname: "/",
					search: "?sort=newest",
				},
				matchSearch: {
					sort: "newest",
				},
				icon: Sparkles,
				description: "Freshly uploaded",
			},
		],
	},

	{
		title: "Genres",
		defaultOpen: true,
		items: [
			{
				title: "Action",
				to: "/?genre=action",
				icon: Zap,
				description: "Fast-paced combat",
			},
			{
				title: "Adventure",
				to: "/?genre=adventure",
				icon: Rocket,
				description: "Explore new worlds",
			},
			{
				title: "Puzzle",
				to: "/?genre=puzzle",
				icon: Puzzle,
				description: "Challenge your brain",
			},
			{
				title: "Fighting",
				to: "/?genre=fighting",
				icon: Swords,
				description: "Competitive battles",
			},
			{
				title: "Arcade",
				to: "/?genre=arcade",
				icon: Gamepad2,
				description: "Classic arcade fun",
			},
		],
	},

	{
		title: "Community",
		items: [
			{
				title: "Recommended",
				to: "/recommended",
				icon: Star,
				description: "Picked for you",
			},
			{
				title: "Special Mentions",
				to: "/special-mentions",
				icon: Sparkles,
				description: "Hidden gems",
			},
			{
				title: "Devlog Reels",
				to: "/devlog",
				icon: Library,
				description: "Behind the scenes",
			},
			{
				title: "Leaderboards",
				to: "/leaderboards",
				icon: Trophy,
				description: "Top players",
			},
		],
	},

	{
		title: "Creator Tools",
		items: [
			{
				title: "Upload Game",
				to: "/upload",
				icon: Upload,
				description: "Publish your game",
			},
			{
				title: "Dashboard",
				to: "/dashboard",
				icon: LayoutDashboard,
				description: "Manage projects",
			},
		],
	},
];

const SidebarSection = ({ section, onClose }) => {
	const [open, setOpen] = useState(section.defaultOpen ?? true);

	return (
		<div className="space-y-2">
			<button
				onClick={() => setOpen((prev) => !prev)}
				className={cn(
					"flex w-full items-center justify-between",
					"px-2 py-2",
					"text-[11px] font-bold uppercase tracking-[0.15em]",
					"text-muted-foreground"
				)}
			>
				<span>{section.title}</span>

				<ChevronDown
					className={cn(
						"h-4 w-4 transition-transform duration-300",
						open && "rotate-180"
					)}
				/>
			</button>

			<div
				className={cn(
					"overflow-hidden transition-all duration-300 ease-out",
					open ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
				)}
			>
				<div className="space-y-1">
					{section.items.map((item) => (
						<NavLink
							key={item.title}
							to={item.to}
							matchSearch={item.matchSearch}
							end={
								typeof item.to === "string"
									? item.to === "/"
									: item.to?.pathname === "/"
							}
							onClick={onClose}
							className={cn(
								"group relative flex items-center gap-3",
								"rounded-2xl border border-transparent",
								"px-3 py-3",
								"text-sm transition-all duration-200",
								"text-muted-foreground",
								"hover:border-border",
								"hover:bg-accent",
								"hover:text-foreground",
								"hover:translate-x-1"
							)}
							activeClassName={cn(
								"bg-secondary text-foreground",
								"border-border shadow-card"
							)}
						>
							<div
								className={cn(
									"flex h-11 w-11 shrink-0 items-center justify-center",
									"rounded-2xl border border-border",
									"bg-card transition-all duration-200",
									"group-hover:scale-105",
									"group-hover:bg-secondary"
								)}
							>
								<item.icon className="h-5 w-5" />
							</div>

							<div className="min-w-0 flex-1">
								<p className="truncate font-medium">
									{item.title}
								</p>

								<p className="truncate text-xs text-muted-foreground">
									{item.description}
								</p>
							</div>
						</NavLink>
					))}
				</div>
			</div>
		</div>
	);
};

const AppSidebar = ({ isMobileOpen, onClose }) => {
	return (
		<>
			<div
				className={cn(
					"fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm transition-all duration-300",
					isMobileOpen
						? "opacity-100"
						: "pointer-events-none opacity-0"
				)}
				onClick={onClose}
				aria-hidden="true"
			/>

			<aside
				className={cn(
					"fixed left-0 top-16 bottom-0 z-50 w-72",
					"border-r border-border",
					"bg-background",
					"transition-transform duration-300 ease-out",
					"shadow-2xl",
					isMobileOpen ? "translate-x-0" : "-translate-x-full"
				)}
			>
				<nav className="flex h-full flex-col">
					<div
						className={cn(
							"sticky top-0 z-10",
							"border-b border-border",
							"bg-background",
							"px-4 py-4"
						)}
					>
						<div className="flex items-start justify-between">
							<div className="flex items-center gap-3">
								<div
									className={cn(
										"flex h-12 w-12 items-center justify-center",
										"rounded-2xl border border-border",
										"bg-card shadow-card"
									)}
								>
									<Coffee className="h-5 w-5 text-primary" />
								</div>

								<div>
									<h2 className="text-base font-semibold text-foreground">
										Coffee Game
									</h2>

									<p className="text-xs text-muted-foreground">
										Indie discovery platform
									</p>
								</div>
							</div>

							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={onClose}
								className={cn(
									"rounded-xl text-muted-foreground",
									"hover:bg-accent hover:text-foreground"
								)}
							>
								<X className="h-4 w-4" />
							</Button>
						</div>
					</div>

					<div className="flex-1 overflow-y-auto px-3 py-4">
						<div className="space-y-6">
							{navSections.map((section) => (
								<SidebarSection
									key={section.title}
									section={section}
									onClose={onClose}
								/>
							))}
						</div>
					</div>

					<div
						className={cn(
							"border-t border-border",
							"bg-card/40 px-4 py-4"
						)}
					>
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm font-medium text-foreground">
									Coffee Game Online
								</p>

								<p className="text-xs text-muted-foreground">
									Play • Upload • Discover
								</p>
							</div>

							<div
								className={cn(
									"rounded-xl bg-secondary px-2 py-1",
									"text-[10px] font-bold uppercase tracking-wide",
									"text-foreground"
								)}
							>
								Beta
							</div>
						</div>
					</div>
				</nav>
			</aside>
		</>
	);
};

export default AppSidebar;